import { usePubNub } from "pubnub-react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { APP_CONFIG } from "../../../constants/constants";
import { formattedDateTime } from "../../../utils/dateTime";
import { getUserIntials } from "../../../utils/string";
import { useChatContext } from "../contexts/chat.context";
import { TPubNubConversationMessage, TPubNubFile, TPubNubTextMessage } from "../contexts/types/pubnubChat";
import ChatAvatar from "./chatAvatar";

const getChatMessageTimeDisplay = (timetoken: number) => {
  const milliseconds = timetoken / 10000;
  const messageTime = new Date(milliseconds);
  const format = 'LLL dd, yyyy h:mmaaa';
  if (messageTime.getDate() === new Date().getDate()) {
    // TODO: needs work
    return formattedDateTime(messageTime, format); //humanReadableTimeDuration(milliseconds, true);
  }
  return formattedDateTime(messageTime, format);
}

type TChatConversationMessageProps = {
  message: TPubNubConversationMessage;
  messageClass?: string;
  wrapperClass?: string;
}

const ChatConversationMessage: FC<TChatConversationMessageProps> = ({
  message,
  messageClass = "rounded-md text-heading-gray444 text-sm px-4 py-2",
  wrapperClass = ""
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'chatSection' });
  const { t: tMain } = useTranslation();
  const pubnub = usePubNub();

  const {
    subscribedChannels,
    selectedChatChannel,
  } = useChatContext();

  const thisChannel = subscribedChannels.find(channel => channel.channel_id === selectedChatChannel);
  const senderId = (message.entry as TPubNubFile)?.message?.sender || (message.entry as TPubNubTextMessage)?.sender;
  const senderMember = thisChannel?.members_info?.find(member => member.unique_id === senderId);

  const messageByMe = senderId === pubnub.getUUID();

  const avatarInitials = getUserIntials(
    [
      senderMember?.name,
    ].find(item => !!item)
    ?? '?' // fallback
  );

  const messageFile = (message.entry as TPubNubFile)?.file || null;
  const [messageFileUrl, setMessageFileUrl] = useState<string | null>(null);
  const messageFileDetails =
    typeof (message.entry as TPubNubFile)?.message?.message === 'string'
      ? (message.entry as TPubNubFile)?.message
      : null;
  const messageFileIsImage = messageFileDetails?.filetype?.startsWith('image') || false;

  // if message is a file, show the file details
  const fetchFileDetails = async () => {
    try {
      const fileUrl = pubnub.getFileUrl({
        channel: selectedChatChannel,
        id: messageFile.id,
        name: messageFile.name,
      })
      setMessageFileUrl(fileUrl);
      console.log('fileUrl', fileUrl);
    } catch (error) {
      console.error('error:', error);
    }
  }
  useEffect(() => {
    if (!!messageFile) {
      fetchFileDetails();
    }
  }, [messageFile]);

  // download file
  const downloadFile = async () => {
    if (!!messageFileUrl && !!messageFile) {
      try {
        const file = await pubnub.downloadFile({
          channel: selectedChatChannel,
          id: messageFile?.id,
          name: messageFile?.name,
        });
        let fileContent = await file.toBlob();
        const url = URL.createObjectURL(fileContent);
        const a = document.createElement('a');
        a.href = url;
        a.download = messageFile.name;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('[PUBNUB] download error:', error);
      }
    } else {
      if (APP_CONFIG.DEBUG.ALL) console.error('File not found');
    }
  }

  return (
    <div className={`relative flex gap-2 items-start justify-start${messageByMe ? ' flex-row-reverse' : ''} ${wrapperClass}`}>

      {/* Avatar */}
      {/* <AppAvatar
        bgColorClass={messageByMe
          ? 'bg-gray-300'
          : 'bg-accent-blue-pale'
        }
        extraClasses="flex-shrink-0"
        initials={avatarInitials}
      /> */}
      <ChatAvatar
        isMe={messageByMe}
        title={senderMember?.name || senderId}
      />

      {/* Message */}
      <span className={`relative flex flex-col mb-3 ${messageByMe ? "bg-gray-200" : "bg-accent-blue-pale"} ${messageClass}`}>{/* mb-3 to accommodate the date display */}
        {
          typeof message.entry === 'string'
            ? message.entry
            : typeof (message.entry as TPubNubFile)?.message?.message === 'string'
              ? `${messageFileIsImage ? t('image') : t('file')}: ` + (message.entry as TPubNubFile)?.file?.name
              : typeof (message.entry as TPubNubTextMessage)?.message === 'string'
                ? (message.entry as TPubNubTextMessage)?.message
                : JSON.stringify((message.entry as TPubNubTextMessage)?.message)
        }
        {messageFileUrl && (
          <>
            {messageFileIsImage ? (
              <a
                className="flex flex-col"
                href={messageFileUrl}
                target="_blank"
                rel="noreferrer"
                onClick={e => {
                  e.preventDefault();
                  downloadFile();
                }}
                title={`${t('image_click_to_download')} ${messageFileDetails?.filename}`}
              >
                <img
                  className="max-w-[calc(25vw)] max-h-[calc(25vh)] object-cover mt-2"
                  src={messageFileUrl}
                  alt={messageFileDetails?.filename}
                />
                <span className="italic mt-2 text-xs text-black/25">{t('image_click_to_download')}</span>
              </a>
            ) : (
              <AdminFormFieldSubmit
                label={
                  <>
                    {tMain('download')}
                    <img
                      src={svgIcons.Download}
                      alt="Download"
                      className="size-4 inline-block ms-2 group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                    />
                  </>
                }
                onClick={downloadFile}
                type="button"
                variant="primary"
                customWrapperClass="mt-2 w-36"
                buttonDimensionClass={"h-10 w-full px-4 bg-white"}
              />
            )}
          </>
        )}
        <span className={`absolute text-[8px] top-full -mt-0.5 ${messageByMe ? 'right-0' : 'left-0'} text-black/40 whitespace-nowrap`}>
          {getChatMessageTimeDisplay(message.timetoken)}
        </span>
      </span>
    </div>
  );
}
export default ChatConversationMessage;